import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useChatMessages } from '@chainlit/react-client';

import Alert from '@/components/Alert';
import { Kbd } from '@/components/Kbd';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar';

import { Translator } from '../i18n';
import { ThreadRowsSkeleton, ThreadSearchResult } from './searchShared';

/**
 * The search box at the top of the history list — the only history search
 * (the Cmd+K dialog was removed: two searches doing the same thing).
 * Ctrl/Cmd+K opens the sidebar if needed and puts the cursor here. The key is
 * matched on event.code, so it works with an Arabic keyboard layout too.
 */
export function SidebarSearchInput({
  value,
  onChange
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { isMobile, setOpen, setOpenMobile } = useSidebar();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      if (e.code !== 'KeyK' && e.key?.toLowerCase() !== 'k') return;
      e.preventDefault();
      if (isMobile) setOpenMobile(true);
      else setOpen(true);
      // The sidebar may be sliding in; focus once it is laid out.
      window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 60);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile, setOpen, setOpenMobile]);

  return (
    <div className="relative px-2 pb-1 pt-1">
      <SidebarInput
        ref={inputRef}
        id="sidebar-thread-search"
        value={value}
        dir="auto"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onChange('');
        }}
        placeholder={t('threadHistory.sidebar.filters.placeholder')}
        className="pr-12"
      />
      {!value && !isMobile ? (
        <Kbd className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 opacity-70">
          Cmd+k
        </Kbd>
      ) : null}
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('threadHistory.search.clear')}
          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Results of the sidebar search, in place of the dated list. Paging is driven
 * by the sidebar's own scroll handler (ThreadHistory).
 */
export function SidebarSearchResults({
  search
}: {
  search: {
    results: import('@chainlit/react-client').IThread[];
    loading: boolean;
    loadingMore: boolean;
    error?: string;
  };
}) {
  const { threadId: currentThreadId } = useChatMessages();
  const { results, loading, loadingMore, error } = search;

  if (loading) return <ThreadRowsSkeleton rows={5} />;
  if (error) {
    return (
      <Alert variant="error" className="m-3">
        {error}
      </Alert>
    );
  }
  if (results.length === 0) {
    return (
      <div className="px-3 py-4 text-sm text-muted-foreground">
        <Translator path="threadHistory.sidebar.filters.noResults" />
      </div>
    );
  }
  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {results.map((thread) => (
            <SidebarMenuItem key={thread.id} id={`search-thread-${thread.id}`}>
              <Link to={`/thread/${thread.id}`}>
                <SidebarMenuButton
                  isActive={currentThreadId === thread.id}
                  className="h-auto items-start py-1.5"
                >
                  <ThreadSearchResult thread={thread} />
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
        {loadingMore ? <ThreadRowsSkeleton rows={2} /> : null}
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
